import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Activity, AlertTriangle, Users, TrendingUp, HeartPulse, Target } from 'lucide-react';
import SeasonSwitcher from '../components/SeasonSwitcher';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (v) =>
  v === null || v === undefined || Number.isNaN(Number(v)) ? '—' : Math.round(Number(v));

const AcademyOverview = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [seasonId, setSeasonId] = useState('');

  useEffect(() => {
    const h = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
    setLoading(true);
    const params = seasonId ? `?season_id=${encodeURIComponent(seasonId)}` : '';
    axios
      .get(`${API}/api/analytics/academy/overview${params}`, h)
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load academy analytics'))
      .finally(() => setLoading(false));
  }, [seasonId]);

  return (
    <Layout>
      <div className="space-y-4" data-testid="academy-overview-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Activity className="text-emerald-600" size={22} />
              Academy Overview
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Live, read-only academy health — active athletes, squad distribution, and high-risk list.
            </p>
          </div>
          <SeasonSwitcher value={seasonId} onChange={setSeasonId} userRole={user?.role} testId="academy-season-switcher" />
        </div>

        {loading ? (
          <div className="py-16 text-center text-sm text-gray-500">Loading…</div>
        ) : !data ? (
          <Card data-testid="academy-no-data">
            <CardContent className="py-16 text-center text-sm text-gray-500 dark:text-gray-400">
              Academy overview is unavailable right now. Please refresh in a moment.
            </CardContent>
          </Card>
        ) : (
          <>
            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="academy-kpi-row">
              <KpiCard
                label="Total Active Athletes"
                value={data.total_active_athletes}
                icon={Users}
                iconColor="text-blue-600"
                testId="academy-kpi-active"
              />
              <KpiCard
                label="High-Risk Players"
                value={data.high_risk_count}
                icon={AlertTriangle}
                iconColor="text-amber-600"
                valueClass="text-amber-600 dark:text-amber-400"
                testId="academy-kpi-high-risk"
              />
              <KpiCard
                label="Avg Form"
                value={fmt(data.averages?.form_index)}
                icon={TrendingUp}
                iconColor="text-emerald-600"
                testId="academy-kpi-form"
              />
              <KpiCard
                label="Avg Availability"
                value={fmt(data.averages?.availability_score)}
                icon={HeartPulse}
                iconColor="text-rose-600"
                testId="academy-kpi-availability"
              />
              <KpiCard
                label="Avg Development"
                value={fmt(data.averages?.development_score)}
                icon={Target}
                iconColor="text-purple-600"
                testId="academy-kpi-development"
              />
            </div>

            {/* Squad distribution */}
            <Card data-testid="academy-squad-distribution">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Squad Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {Object.keys(data.squad_distribution || {}).length === 0 ? (
                    <p className="text-xs text-gray-400">No squads</p>
                  ) : (
                    Object.entries(data.squad_distribution).map(([squad, count]) => (
                      <div
                        key={squad}
                        className="px-3 py-2 rounded-md bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
                        data-testid={`academy-squad-${squad}`}
                      >
                        <p className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          {squad}
                        </p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-gray-100 tabular-nums">
                          {count}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* High-risk list */}
            <Card data-testid="academy-high-risk-list">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <AlertTriangle size={14} className="text-amber-600" />
                  High-Risk Players
                  <span className="text-[11px] font-normal text-gray-500 dark:text-gray-400">
                    (availability &lt;50 or development &lt;50)
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(data.high_risk_athletes || []).length === 0 ? (
                  <p className="py-6 text-center text-xs text-gray-400">
                    No high-risk athletes — nice work.
                  </p>
                ) : (
                  <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                    {data.high_risk_athletes.map((a) => (
                      <li
                        key={a.athlete_id}
                        className="flex items-center justify-between py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 -mx-3 px-3 rounded"
                        onClick={() => navigate(`/athlete/${a.athlete_id}/360`)}
                        data-testid={`academy-high-risk-row-${a.athlete_id}`}
                      >
                        <div className="min-w-0">
                          <p className="text-xs text-gray-800 dark:text-gray-200 truncate">
                            {a.athlete_name}
                          </p>
                          <p className="text-[10px] text-gray-400 mt-0.5">{a.squad || 'Unassigned'}</p>
                        </div>
                        <div className="flex gap-1 flex-wrap justify-end">
                          {(a.reasons || []).map((r) => (
                            <Badge
                              key={r}
                              className="text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                            >
                              {r}
                            </Badge>
                          ))}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                {data.high_risk_count > (data.high_risk_athletes || []).length ? (
                  <p className="text-[10px] text-gray-400 mt-2 text-right">
                    Showing {data.high_risk_athletes.length} of {data.high_risk_count}
                  </p>
                ) : null}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
};

const KpiCard = ({ label, value, icon: Icon, iconColor, valueClass, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="pt-4 pb-3">
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {label}
        </p>
        <Icon size={14} className={iconColor} />
      </div>
      <p
        className={`text-3xl font-semibold mt-1 tabular-nums ${
          valueClass || 'text-gray-900 dark:text-gray-100'
        }`}
      >
        {value === null || value === undefined ? '—' : value}
      </p>
    </CardContent>
  </Card>
);

export default AcademyOverview;
